<template>
  <div>
    <main-layout
      :showPoweredBy="true"
      controlsBg="light"
      :hasControlsBar="false"
      :breadcrumb="getBreadcrumb">
    <div class="container">
      <grid-row  class="margin-top-xl" column-align="center">
        <grid-col col-size="4">
          <div class="header-wrapper">
            <div class="title-wrapper">
              <chevron
                @click="back()"/>
              <h1 class="header-title">
                {{ $t('explorer.parameterSelection') }}
              </h1>
              <h2>{{ $t('menu.correlation') }}</h2>
            </div>
            <div>
              <btn-pill
                v-has-feature="'Query'"
                v-on:click="goToQueries()"
                btn-size="s"
                btn-type="secondary">
                <font-awesome-icon :icon="icon.queries"/>
                {{ $t('menu.queries') }}
              </btn-pill>
              <btn-pill
                v-has-feature="'AggregateVariable'"
                v-on:click="goToAggregateVariables()"
                btn-size="s"
                btn-type="secondary">
                <font-awesome-icon :icon="icon.aggregateVariables "/>
                {{ $t('aggregateVariable.addAggregateVariable') }}
              </btn-pill>
            </div>
          </div>
        </grid-col>
      </grid-row>
      <grid-row  class="margin-top-xl" column-align="center">
        <grid-col col-size="4">
          <div class="selection-title">
            {{ $t('explorer.selectionVariables') }}
          </div>
          <transition-group name="fade">
            <div
              class="selection-wrapper"
              v-for="(selectedVariable, index) in selectedVariables" :key="index">
              <div class="flex">
                <span class="variable-name">X:</span>
                <variable-selector
                  :id="'selectX' + index"
                  class="selection-left"
                  v-model="selectedVariable.modelX"
                  v-on:tagsChanged="tagsChanged"
                  :variables="getVariablesMinimal"
                  :taggedVariables="getTaggedVariables"
                  :tree="getTagsTree"/>
                <span class ="exchange">
                  <font-awesome-icon
                    v-on:click="exChangeVariables(index)"
                    :icon="icon.exchange"/>
                </span>
                <span class="variable-name">Y:</span>
                <variable-selector
                  class="selection-left"
                  v-model="selectedVariable.modelY"
                  v-on:tagsChanged="tagsChanged"
                  :taggedVariables="getTaggedVariables"
                  :variables="getVariablesMinimal"
                  :tree="getTagsTree"/>
                <font-awesome-icon
                  :class="isRemoveButtonDisabled() ? 'selection-right-disabled' : 'selection-right'"
                  v-on:click="onClickRemoveVariablePair(index)"
                  :icon="icon.remove"/>
              </div>
              <transition name="fade">
                <div
                  v-show="radioOptions.checkedIndividualCategory"
                  class="selection-category-wrapper">
                  <span class="variable-name">{{ $t('explorer.categories') }}:</span>
                  <variable-selector
                    :id="'selectCategory' + index"
                    class="selection-left"
                    v-on:tagsChanged="tagsChanged"
                    :taggedVariables="getTaggedCategorizedVariables"
                    v-model="selectedCategoryVariables[index]"
                    :variables="getZVariables"
                    :tree="getTagsTree"/>
                </div>
              </transition>
            </div>
          </transition-group>
          <div
            class="add-wrapper">
            <btn-pill
              class="add-btn"
              :disabled="isAddButtonDisabled()"
              v-on:click="onClickAddVariablePair()"
              btn-size="s"
              btn-type="secondary">
              <font-awesome-icon :icon="icon.add"/>
              {{ $t('explorer.addVariables') }}
            </btn-pill>
          </div>
          <div class="form-group intensity-group">
            <span class="check-group">
              <span class="variable-name">Z:</span>
              <radio
                v-for="radioOption in Object.keys(radioOptions)"
                v-bind:key="'radio' + radioOption"
                class="check"
                :id="radioOption"
                style="margin-bottom: 0;"
                :checked="radioOptions[radioOption] ? 'checked' : ''"
                :label="$t('explorer.' + radioOption)"
                v-on:change="onCheckChanged(radioOption)">
              </radio>
            </span>
            <transition name="fade">
              <div
                v-show="radioOptions.checkedIntensity || radioOptions.checkedGlobalCategory"
                class="selection-intensity-wrapper">
                <variable-selector
                  id="selectZ"
                  class="selection-left"
                  @input="assignSameCategory"
                  v-model="selectedZVariable"
                  :variables="getZVariables"
                  v-on:tagsChanged="tagsChanged"
                  :taggedVariables="getTaggedCategorizedVariables"
                  :tree="getTagsTree"/>
              </div>
            </transition>
          </div>
          <div class="selection-title">
            {{ $t('explorer.datesSelection') }}
          </div>
          <date-range-selector
            ref="dateRangeSelector"
            v-model="selectedDates"/>
          <div
            class="action-wrapper">
            <btn-pill
              v-on:click="onClickCancel()"
              :btnText="$t('commons.cancel')"
              btn-size="m"
              btn-type="secondary">
            </btn-pill>
            <btn-pill
              class="margin-left-s"
              :disabled="isExploreDisabled()"
              v-on:click="onClickExplore()"
              :btnText="$t('explorer.menu.explore')"
              btn-size="m"
              btn-type="primary">
            </btn-pill>
          </div>
        </grid-col>
      </grid-row>
    </div>
    </main-layout>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import MainLayout from '../MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import BtnPill from '@/components/shared/buttons/BtnPill';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import {
  faTimesCircle,
  faPlusCircle,
  faExchangeAlt
} from '@fortawesome/pro-solid-svg-icons';
import {
  faCalculatorAlt,
  faFileChartLine
} from '@fortawesome/pro-light-svg-icons';
import Checkbox from '@/components/shared/forms/Checkbox';
import DateRangeSelector from '@/components/explorer/DateRangeSelector';
import VariableSelector from '@/components/variable/VariableSelector';
import Chevron from '@/components/shared/Chevron';
import Radio from '@/components/shared/forms/Radio';
import variableApi from '@/api/variable';
import variableSelectorUtils from '@/layouts/variableSelectorUtils';

export default {
  name: 'ExplorerCorrelationVariables',
  components: {
    VariableSelector,
    MainLayout,
    GridRow,
    GridCol,
    BtnPill,
    FontAwesomeIcon,
    Checkbox,
    DateRangeSelector,
    Chevron,
    Radio
  },
  mixins: [
    variableSelectorUtils
  ],
  data () {
    return {
      selectedDates: [],
      selectedVariables: [],
      selectedCategoryVariables: [null],
      selectedZVariable: undefined,
      radioOptions: {
        none: false,
        checkedIntensity: false,
        checkedIndividualCategory: false,
        checkedGlobalCategory: false
      },
      icon: {
        add: faPlusCircle,
        remove: faTimesCircle,
        queries: faFileChartLine,
        aggregateVariables: faCalculatorAlt,
        exchange: faExchangeAlt
      },
      variablesMinimal: [],
      taggedVariables: []
    };
  },
  created () {
    this.retrieveTagsTree({ context: this });
    variableApi.getMinimalVariables(this).then(data => {
      this.taggedVariables = this.variablesMinimal = data.body.sort((a, b) => {
        return a.label.localeCompare(b.label, undefined, {numeric: true, sensitivity: 'base'});
      });
      this.onRetrieveVariables();
    });
  },
  updated () {
    // Tricky way to set Z width
    if (document.getElementById('selectX0')) {
      document.getElementById('selectZ').style.width = document.getElementById('selectX0').offsetWidth + 'px';
      document.querySelectorAll('[id*=selectCategory]').forEach(selector => {
        selector.style.width = document.getElementById('selectX0').offsetWidth + 'px';
      });
    }
  },
  computed: {
    ...mapGetters([
      'getTagsTree'
    ]),
    getBreadcrumb () {
      return [
        {name: this.$t('explorer.breadcrumbParentOldExplorer'), link: '/explore'},
        {name: this.$t('explorer.parameterSelection'), disabled: true}
      ];
    },
    getZVariables () {
      if (this.radioOptions.checkedIndividualCategory || this.radioOptions.checkedGlobalCategory) {
        return this.getVariablesMinimal.filter(variable => variable.category);
      }
      return this.getVariablesMinimal;
    },
    getVariablesMinimal () {
      return this.variablesMinimal;
    },
    getTaggedVariables () {
      return this.taggedVariables;
    },
    getTaggedCategorizedVariables () {
      if (this.radioOptions.checkedIndividualCategory || this.radioOptions.checkedGlobalCategory) {
        return this.taggedVariables.filter(variable => variable.category);
      }
      return this.taggedVariables;
    }
  },
  methods: {
    ...mapActions([
      'retrieveTagsTree'
    ]),
    back () {
      this.$router.push({name: 'Explore', query: this.$route.query});
    },
    onRetrieveVariables () {
      const query = this.$route.query;

      // Variables
      for (let i = 1; i <= MAXIMUM_NUMBER_OF_ALLOWED_VARIABLE_PAIRS; i++) {
        const currentPair = query['pair' + i];
        if (currentPair) {
          const currentSelectedVariables = {
            modelX: this.findVariableModelById(currentPair.split(',')[0]),
            modelY: this.findVariableModelById(currentPair.split(',')[1])
          };
          this.selectedVariables.push(currentSelectedVariables);
        }
      }
      if (query.z) {
        if (query.zType === 'category') {
          const categoryIds = query.z.split(',');
          if (categoryIds.every(category => category === categoryIds[0])) {
            if (categoryIds[0] === '_') {
              this.radioOptions.none = true;
            } else {
              this.radioOptions.checkedGlobalCategory = true;
              this.selectedZVariable = this.findVariableModelById(categoryIds[0]);
            }
          } else {
            this.radioOptions.checkedIndividualCategory = true;
            this.selectedCategoryVariables = [];
            categoryIds.forEach(category => {
              if (category !== '_') {
                this.selectedCategoryVariables.push(this.findVariableModelById(Number(category)));
              } else {
                this.selectedCategoryVariables.push(null);
              }
            });
          }
        } else {
          this.radioOptions.checkedIntensity = true;
          this.selectedZVariable = this.findVariableModelById(query.z);
        }
      } else {
        this.radioOptions.none = true;
      }
      if (this.selectedVariables.length === 0) {
        this.selectedVariables.push({
          modelX: undefined,
          modelY: undefined
        });
      }

      // Dates
      const startDate = query.startDate;
      const endDate = query.endDate;
      if (startDate && endDate) {
        this.selectedDates = [new Date(Number(startDate)), new Date(Number(endDate))];
        this.$refs.dateRangeSelector.selectCustomRange();
      }
    },
    goToQueries () {
      this.$router.push({name: 'QueryList', query: {filter: 'explorer-correlation', origin: 'ExplorerCorrelationVariables'}});
    },
    goToAggregateVariables () {
      this.$router.push({name: 'AggregateOperatedVariable', query: {origin: 'ExplorerCorrelationVariables'}});
    },
    isRemoveButtonDisabled () {
      // At least one variable
      return this.selectedVariables.length === 1;
    },
    onClickRemoveVariablePair (index) {
      if (!this.isRemoveButtonDisabled()) {
        this.selectedVariables.splice(index, 1);
        if (this.radioOptions.checkedIndividualCategory) {
          this.selectedCategoryVariables.splice(index, 1);
        }
      }
    },
    isAddButtonDisabled () {
      let disabled = false;
      const size = this.selectedVariables.length;
      for (let i = 0; i < size; i++) {
        const modelXnotSelected = this.selectedVariables[i].modelX === undefined || this.selectedVariables[i].modelX === null;
        const modelYnotSelected = this.selectedVariables[i].modelY === undefined || this.selectedVariables[i].modelY === null;
        if (modelXnotSelected || modelYnotSelected) {
          disabled = true;
          break;
        }
      }
      if (size >= MAXIMUM_NUMBER_OF_ALLOWED_VARIABLE_PAIRS) {
        disabled = true;
      }
      return disabled;
    },
    onClickAddVariablePair () {
      this.selectedVariables.push({
        modelX: undefined,
        modelY: undefined
      });
      this.selectedCategoryVariables.push(null);
    },
    onCheckChanged (option) {
      Object.keys(this.radioOptions).forEach(radioOption => {
        this.radioOptions[radioOption] = radioOption === option;
      });
      this.selectedZVariable = undefined;
    },
    onClickExplore () {
      const selection = this.getSelection();
      const query = {
        startDate: this.selectedDates[0].getTime(),
        endDate: this.selectedDates[1].getTime()
      };

      const pairs = selection.pairs;
      for (let i = 0; i < pairs.length; i++) {
        const pair = pairs[i];
        query['pair' + (i + 1)] = pair.x + ',' + pair.y;
      }

      if (selection.z) {
        query.z = Array.isArray(selection.z) ? selection.z.join(',') : selection.z;
        query.zType = this.radioOptions.checkedIndividualCategory || this.radioOptions.checkedGlobalCategory ? 'category' : 'intensity';
      }
      const oldQuery = this.$route.query;
      for (let i in oldQuery) {
        if (oldQuery.hasOwnProperty(i) && i.startsWith('opt')) {
          query[i] = oldQuery[i];
        }
      }
      this.$router.push({path: 'explorer-correlation', query: query});
    },
    onClickCancel () {
      this.selectedZVariable = undefined;
      this.selectedCategoryVariables = [];
      this.radioOptions.checkedIntensity = false;
      this.radioOptions.checkedIndividualCategory = false;
      this.selectedVariables = [{
        modelX: undefined,
        modelY: undefined
      }];
    },
    isExploreDisabled () {
      let disabled = this.selectedVariables.some(variable => variable.modelX === undefined || variable.modelY === undefined);
      if (this.radioOptions.checkedIntensity) {
        disabled = disabled || !this.selectedZVariable || this.selectedZVariable === null;
      }
      return disabled;
    },
    getSelection () {
      const selectionSet = {
        pairs: []
      };
      for (let i = 0; i < this.selectedVariables.length; i++) {
        const candidateX = this.selectedVariables[i].modelX;
        const candidateY = this.selectedVariables[i].modelY;
        if (candidateX && candidateY) {
          selectionSet.pairs.push({x: candidateX.id, y: candidateY.id});
        }
      }

      if (this.radioOptions.checkedIntensity) {
        selectionSet.z = this.selectedZVariable.id;
      } else if (this.radioOptions.checkedIndividualCategory || this.radioOptions.checkedGlobalCategory) {
        selectionSet.z = this.selectedCategoryVariables.map(variable => variable !== null ? variable.id : '_');
      }
      return selectionSet;
    },
    findVariableModelById (id) {
      const variables = this.getVariablesMinimal.slice();
      for (let i = 0; i < variables.length; i++) {
        if (Number(id) === variables[i].id) {
          return variables[i];
        }
      }
      return undefined;
    },
    exChangeVariables (index) {
      let aux = this.selectedVariables[index].modelX;
      this.selectedVariables[index].modelX = this.selectedVariables[index].modelY;
      this.selectedVariables[index].modelY = aux;
    },
    assignSameCategory (category) {
      if (this.radioOptions.checkedGlobalCategory) {
        this.selectedCategoryVariables = [];
        for (let i = 0; i < this.selectedVariables.length; i++) {
          this.selectedCategoryVariables.push(category);
        }
      }
    }
  }
};

const MAXIMUM_NUMBER_OF_ALLOWED_VARIABLE_PAIRS = 4;

</script>

<style lang="scss" scoped>
  @import "src/stylesheets/variables";
  .header-wrapper {
    display: flex;
    justify-content: space-between;
  }

  .title-wrapper {
    display: flex;
  }

  h1 {
    margin-right: $sp-margin;
    padding-right: $sp-margin;
    border-right: 1px solid $black-trans6;
  }

  h2 {
    line-height: 2.2;
    font-size: 1.2rem;
    font-weight: lighter;
  }

  .selection-title {
    font-size: 1.2rem;
    margin-bottom: $sp-margin-small;
  }

  .selection-wrapper {
    background-color: transparent;
    display: block;
    justify-content: space-between;
    align-items: center;
    margin-bottom: $sp-margin-smaller;
  }

  .selection-left {
    width: 50%;
    margin-right: $sp-margin-small;
  }

  .selection-right {
    font-size: $fs-xl;
    cursor: pointer;
    color: $primary;
  }

  .selection-right-disabled {
    visibility: hidden;
    font-size: $fs-xl;
    color: $primary;
  }

  .add-wrapper {
    margin-top: $sp-margin-small;
  }

  .add-btn {
    margin-bottom: $sp-margin-small;
  }

  .intensity-group {
    margin-top: $sp-margin-small;
    margin-bottom: $sp-margin-larger;
  }

  .selection-intensity-wrapper {
    display: flex;
    align-items: center;
    margin-top: $sp-margin-small;
    width: 47%;
  }

  .selection-category-wrapper {
    display: flex;
    align-items: center;
    margin-top: $sp-margin-small;
    margin-bottom: $sp-margin-large;
    width: 47%;
  }

  .variable-name {
    margin-right: $sp-margin-small;
  }

  .action-wrapper {
    float: right;
    margin-top: $sp-margin-small;
    margin-bottom: $sp-margin-larger;
  }

  .exchange {
    margin: $sp-margin;
    font-size: $fs-l;
  }

  .exchange:hover {
    cursor: pointer;
    opacity: 0.3;
    transition: opacity 0.3s;
  }

  .check-group {
    display: flex;
  }

  .check {
    margin-right: $sp-margin-large;
  }

  .flex {
    display: flex;
    align-items: center;
  }

  #selectZ {
    margin-left: $sp-margin;
  }
</style>



// WEBPACK FOOTER //
// src/layouts/explorer/ExplorerCorrelationVariables.vue